/**
 * Campagnes — règles PURES partagées par la liste, le détail et les tests.
 *
 * Une campagne est un audit consolidé : un code unique pour N machines, un
 * seul dossier, une seule facture (docs/CAMPAGNE_fr.md). Ce module ne connaît
 * ni React ni Firestore ; il tient les décisions que l'interface doit prendre
 * de la même façon que les fonctions serveur.
 *
 * Le **code n'apparaît nulle part côté données** : il n'existe que dans la
 * réponse de `createCampaign`. Toutes les commandes affichées retombent donc
 * sur {@link CAMPAIGN_CODE_PLACEHOLDER} quand l'administrateur ne l'a plus
 * sous la main — jamais sur une valeur relue depuis Firestore.
 */
import type { BadgeVariant } from '@/components/ui/badge';
import type {
  CampaignHostDoc, CampaignHostStatus, CampaignStatus, ConsolidatedSummary, Role,
} from '@/types/models';
import type { messages as MSG } from './i18n.messages';

/** Marqueur affiché à la place du code quand il n'est plus connu de la session. */
export const CAMPAIGN_CODE_PLACEHOLDER = '<code>';

/** Bornes du serveur (`lib/campaigns.py`) reprises par le formulaire de création. */
export const MAX_HOSTS_EXPECTED = 200;
export const MAX_HOST_SLUG_LENGTH = 128;

/**
 * Commande à lancer sur CHAQUE machine de la campagne : le même code partout.
 * Sans code connu, le gabarit reste lisible (`auditai --code <code>`).
 */
export function campaignCommand(code?: string | null): string {
  return `auditai --code ${code || CAMPAIGN_CODE_PLACEHOLDER}`;
}

/**
 * Slug de machine, identique à `host_slug` côté fonctions : minuscules,
 * caractères hors `[0-9a-z._-]` remplacés par un tiret, bords nettoyés.
 * Rend `''` quand il ne reste rien — l'appelant écarte alors l'entrée.
 */
export function hostSlug(raw: string): string {
  return raw.trim().toLowerCase()
    .replace(/[^0-9a-z._-]+/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '')
    .slice(0, MAX_HOST_SLUG_LENGTH);
}

/**
 * Saisie libre du formulaire (virgules, points-virgules, retours à la ligne)
 * → liste de slugs uniques et bornée, comme `normalize_hosts_expected`.
 *
 * On ne coupe PAS sur les espaces : « SRV Fichiers » est un seul nom de
 * machine, que {@link hostSlug} ramène à `srv-fichiers`.
 */
export function parseHostsInput(raw: string): string[] {
  const out: string[] = [];
  for (const part of raw.split(/[\r\n,;]+/)) {
    const slug = hostSlug(part);
    if (slug && !out.includes(slug) && out.length < MAX_HOSTS_EXPECTED) out.push(slug);
  }
  return out;
}

/** Les cinq états du suivi machine par machine, dans l'ordre d'avancement. */
export const CAMPAIGN_HOST_STATUSES: CampaignHostStatus[] = [
  'pending', 'uploading', 'processing', 'ready', 'failed',
];

const HOST_STATUS_KEY = {
  pending:    'campaign.host.status.pending',
  uploading:  'campaign.host.status.uploading',
  processing: 'campaign.host.status.processing',
  ready:      'campaign.host.status.ready',
  failed:     'campaign.host.status.failed',
} as const satisfies Record<CampaignHostStatus, keyof typeof MSG>;

/** Clé i18n de l'état d'une machine ; « attendue » pour un statut inconnu. */
export function hostStatusKey(status?: string | null): keyof typeof MSG {
  return HOST_STATUS_KEY[status as CampaignHostStatus] ?? HOST_STATUS_KEY.pending;
}

/** Couleur du badge d'état d'une machine. */
export function hostStatusVariant(status?: string | null): BadgeVariant {
  switch (status) {
    case 'ready': return 'success';
    case 'failed': return 'danger';
    case 'processing':
    case 'uploading': return 'info';
    default: return 'neutral';
  }
}

const CAMPAIGN_STATUS_KEY = {
  open:       'campaign.status.open',
  processing: 'campaign.status.processing',
  ready:      'campaign.status.ready',
  closed:     'campaign.status.closed',
} as const satisfies Record<CampaignStatus, keyof typeof MSG>;

/** Clé i18n du statut d'une campagne ; « ouverte » pour un statut inconnu. */
export function campaignStatusKey(status?: string | null): keyof typeof MSG {
  return CAMPAIGN_STATUS_KEY[status as CampaignStatus] ?? CAMPAIGN_STATUS_KEY.open;
}

/** Couleur du badge de statut d'une campagne. */
export function campaignStatusVariant(status?: string | null): BadgeVariant {
  switch (status) {
    case 'ready': return 'success';
    case 'processing': return 'info';
    case 'closed': return 'neutral';
    default: return 'warning';
  }
}

/** Compteur par état pour l'en-tête du suivi (total inclus). */
export function campaignHostCounts(
  hosts: CampaignHostDoc[],
): Record<CampaignHostStatus, number> & { total: number } {
  const counts = { pending: 0, uploading: 0, processing: 0, ready: 0, failed: 0, total: hosts.length };
  for (const host of hosts) {
    const status = (CAMPAIGN_HOST_STATUSES as string[]).includes(host.status) ? host.status : 'pending';
    counts[status as CampaignHostStatus] += 1;
  }
  return counts;
}

/**
 * Une machine qui manque encore à l'appel — machine annoncée jamais reçue, ou
 * machine VUE par le collecteur central sans audit propre.
 */
export interface MissingCampaignHost {
  host: string;
  /** Code de motif du collecteur (`not_reported` pour une machine annoncée). */
  reason: string;
  hint: string;
  /** Commande à lancer sur cette machine (le code y est injecté quand il est connu). */
  command: string;
  /** Machines depuis lesquelles celle-ci a été aperçue. */
  seenFrom: string[];
  /** Vrai si elle avait été annoncée à la création de la campagne. */
  expected: boolean;
}

/**
 * Machines manquantes, calculées comme `missing_hosts` côté fonctions : union
 * des machines vues non auditées (module 09) et des machines annoncées qui
 * n'ont pas rendu d'audit. Triée par nom, jamais inventée : sans consolidé,
 * seules les machines annoncées ressortent.
 */
export function missingCampaignHosts(
  consolidated: ConsolidatedSummary | null | undefined,
  hostsExpected: string[] = [],
  code?: string | null,
): MissingCampaignHost[] {
  const coverage = consolidated?.coverage;
  const audited = new Set(coverage?.hostsAudited ?? []);
  const expected = new Set(hostsExpected);
  const out = new Map<string, MissingCampaignHost>();

  for (const entry of coverage?.hostsSeenNotAudited ?? []) {
    if (!entry?.host || audited.has(entry.host)) continue;
    out.set(entry.host, {
      host: entry.host,
      reason: entry.reason ?? '',
      hint: entry.hint ?? '',
      command: campaignCommand(code),
      seenFrom: entry.seenFrom ?? [],
      expected: expected.has(entry.host),
    });
  }
  for (const host of hostsExpected) {
    if (audited.has(host) || out.has(host)) continue;
    out.set(host, {
      host,
      reason: 'not_reported',
      hint: '',
      command: campaignCommand(code),
      seenFrom: [],
      expected: true,
    });
  }
  return [...out.values()].sort((a, b) => a.host.localeCompare(b.host));
}

/**
 * « Clore la campagne » : `admin_global` ferme n'importe laquelle, `org_admin`
 * celles de son organisation. Une campagne déjà close ne se referme pas.
 */
export function canCloseCampaign(role: Role | undefined, status?: string | null): boolean {
  if (status === 'closed') return false;
  return role === 'admin_global' || role === 'org_admin';
}

/**
 * Un consolidé n'est affichable que s'il porte au moins un audit prêt : le
 * serveur écrit `warnings: ['no_ready_audit']` tant que rien n'est fusionné.
 */
export function hasConsolidation(consolidated: ConsolidatedSummary | null | undefined): boolean {
  if (!consolidated) return false;
  return !(consolidated.warnings ?? []).includes('no_ready_audit');
}
